/**
 * Establishment Service
 *
 * Complete service for establishment-service API endpoints
 * Handles establishments, users, menus, products, stock management, etc.
 */

import { format } from 'date-fns';
import { apiClient } from './apiClient';
import authService from './authService';
import type {
  AddStockRequest,
  AttachMenuRequest,
  Bank,
  BankListResponse,
  ClassListResponse,
  CreateClassRequest,
  CreateEstablishmentInternalUserRequest,
  CreateEstablishmentInternalUserResponse,
  CreateEstablishmentProductRequest,
  CreateEstablishmentRequest,
  CreateEstablishmentUserRequest,
  CreateIntervalRequest,
  CreateMenuRequest,
  CreateStockEntryRequest,
  CreateStockEntryResponse,
  CreateStockOutRequest,
  CreateStockOutResponse,
  DeleteEstablishmentUserRequest,
  DetachMenuRequest,
  EstabilishmentProductDetailsParams,
  // Establishment types
  Establishment,
  EstablishmentBalance,
  // Class types
  EstablishmentClass,
  // Interval types
  EstablishmentInterval,
  EstablishmentListResponse,
  EstablishmentLunchboxDashboard,
  EstablishmentLunchboxDashboardParams,
  EstablishmentLunchboxClassDetail,
  EstablishmentLunchboxClassDetailParams,
  // Menu types
  EstablishmentMenu,
  EstablishmentOperatorListResponse,
  // Product types
  EstablishmentProduct,
  EstablishmentProductDetailResponse,
  EstablishmentProductListResponse,
  EstablishmentProductSearchParams,
  EstablishmentProductSuggestionsResponse,
  EstablishmentSearchParams,
  EstablishmentType,
  EstablishmentTypeListResponse,
  // User types
  EstablishmentUser,
  EstablishmentUserGetAllResponse,
  EstablishmentUserListResponse,
  ExecuteStockTransferRequest,
  CancelStockTransferRequest,
  GetDashboardDataParams,
  GetDashboardDataResponse,
  GetEstablishmentCashFlowMovementsParams,
  GetEstablishmentCashFlowMovementsResponse,
  GetEstablishmentCashFlowSummaryParams,
  GetEstablishmentCashFlowSummaryResponse,
  GetEstablishmentExtractParams,
  GetEstablishmentExtractResponse,
  GetEstablishmentSalesQuery,
  GetEstablishmentSalesResponse,
  GetEstablishmentWithdrawsHistoryParams,
  GetEstablishmentWithdrawsHistoryResponse,
  GlobalProductSearchParams,
  IntervalListResponse,
  LinkProductsBatchRequest,
  LinkProductsBatchResponse,
  MenuListParams,
  MenuListResponse,
  MenuPhotoRequest,
  PostEstablishmentWithdrawsDto,
  PostEstablishmentWithdrawsResponse,
  WithdrawConfirmDto,
  WithdrawConfirmResponse,
  WithdrawInitDto,
  WithdrawInitResponse,
  WithdrawDeleteDto,
  WithdrawDeleteResponse,
  ScheduleStockTransferRequest,
  StockListParams,
  StockListResponse,
  StockMovementsParams,
  StockMovementsResponse,
  StockNFsParams,
  StockNFsResponse,
  StockSummaryParams,
  StockSummaryResponse,
  StockExpirationParams,
  StockExpirationResponse,
  StockTransferListResponse,
  StockTransferParams,
  StockTransferRequest,
  StockTransferResponse,
  ScheduleStockTransferResponse,
  UpdateClassRequest,
  UpdateEstablishmentProductRequest,
  UpdateEstablishmentProductRequestParams,
  UpdateEstablishmentProductStatusRequest,
  UpdateEstablishmentRequest,
  UpdateEstablishmentUserRequest,
  UpdateIntervalRequest,
  UpdateMenuRequest,
  GetOrdersReportOverallParams,
  GetOrdersReportOverallResponse,
  GetOrdersReportOverallByIntervalParams,
  GetOrdersReportOverallByIntervalResponse,
  GetOrdersReportByAttendantParams,
  GetOrdersReportByAttendantResponse,
  GetOrdersReportPctUnderTargetParams,
  GetOrdersReportPctUnderTargetResponse,
  CreateEstablishmentFromAdminRequest,
  TaxIntegrationStatus,
  EnableTaxIntegrationRequest,
  ConfigureTaxIntegrationRequest,
  DeleteTaxIntegrationRequest,
  DeleteTaxCertificateRequest,
  UploadTaxCertificateRequest,
  TaxConfigResponse,
  TaxCstListResponse,
  InternalUserCheckResponse,
  InternalMembershipRequest,
  PaytimeInfo,
  Supplier,
  SupplierListParams,
  SupplierListResponse,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  DeleteSupplierRequest,
  DeleteSupplierResponse,
  PayPurchaseRequest,
  PayPurchaseResponse,
  PurchasePixInitRequest,
  PurchasePixInitResponse,
  PurchasePixConfirmRequest,
  PurchasePixConfirmResponse,
  PaymentReceipt,
  ProductListAllResponse,
  TaxOption,
  EstablishmentTotemBillingSummary,
} from './establishmentTypes';
import type { ApiError } from './types';
import { toOneMinuteToMidnightUtil } from '@lib/date.utils';

// Centralized establishment service routes
const ROUTES = {
  ESTABLISHMENT: {
    EXTRACT: '/establishment/extrato',
    CASH_FLOW: {
      SUMMARY: '/establishment/pdv/reports/summary',
      MOVEMENTS: '/establishment/pdv/reports/movements',
    },
    WITHDRAWS: {
      HISTORY: '/establishment/withdraws/history',
      POST: '/establishment/withdraws',
      INIT: '/establishment/withdraw/init',
      CONFIRM: '/establishment/withdraw/confirm',
      DELETE: '/establishment/withdraw/delete',
    },
    LUNCHBOX: {
      GET_DASHBOARD: '/lunchbox/dashboard',
      GET_CLASS_DETAIL: '/lunchbox/class/detail',
    },
    SALES: {
      GET: '/establishment/sales',
    },
    BALANCE: {
      GET: '/establishment/balance',
    },
    CREATE: '/establishment/create',
    UPDATE: '/establishment/update',
    LIST: '/establishment/list',
    LIST_ALL: '/establishment/list/all',
    TYPE: '/establishment/type',
    INSTITUTION: '/establishment/institution',
    BANK: '/establishment/bank',
    USER: {
      LIST: '/establishment/user/list',
      LIST_ALL: '/establishment/user/listall',
      EDIT: '/establishment/user/edit',
      DELETE: '/establishment/user/delete',
      OPERATOR_LIST: '/establishment/user/operator/list',
      CREATE: '/user/internal/create',
      CHECK: '/user/internal/check',
      MEMBERSHIP: '/user/internal/membership',
    },
    INTERVAL: {
      CREATE: '/establishment/interval/create',
      UPDATE: '/establishment/interval/update',
      LIST: '/establishment/interval/list', // + /{establishmentId}
      DELETE: '/establishment/interval/delete', // + /{id}
    },
    TURMA: {
      UPDATE: '/establishment/turma/update',
      LIST: '/establishment/turma/list',
      CREATE: '/establishment/turma/create',
    },
    MENU: {
      CREATE: '/establishment/menu/create',
      UPDATE: '/establishment/menu/update',
      UPLOAD_PHOTO: '/establishment/menu/photo/upload',
      LIST: '/establishment/menu/list',
      BACKOFFICE_LIST: '/establishment/menu/list',
      BACKOFFICE_ATTACH: '/backoffice/establishment/menu-attach',
      BACKOFFICE_DETACH: '/backoffice/establishment/menu-detach',
      ATTACH: '/establishment/menu/attach',
      DETACH: '/establishment/menu/detach',
    },
    PRODUCT: {
      CREATE: '/establishment/product/create',
      LIST: '/establishment/product/list',
      LIST_ALL: '/product/list',
      DETAIL: '/establishment/product/detail',
      UPDATE: '/establishment/product/update',
      UPDATE_STATUS: '/establishment/product/update/status',
      SUGGESTIONS: '/establishment/product/suggestions',
      GLOBAL_LIST: '/establishment/product/global-list',
      LINK_BATCH: '/establishment/product/add-batch',
      DEL_CLASS_PRICE: '/establishment/product/del/class-price',
      DEL_PROFILE_BLOCKED: '/establishment/product/del/profile-blocked',
      DEL_CLASS_BLOCKED: '/establishment/product/class-blocked',
    },
    SUPPLIER: {
      LIST: '/establishment/supplier/list',
      GET: '/establishment/supplier/get',
      CREATE: '/establishment/supplier/create',
      UPDATE: '/establishment/supplier/update',
      DELETE: '/establishment/supplier/delete',
    },
    STOCK: {
      ADD: '/establishment/stock/add',
      SCHEDULE: '/establishment/stock/schedule',
      TRANSFERS: '/establishment/stock/transfers',
      EXECUTE: '/establishment/stock/execute',
      CANCEL: '/establishment/stock/cancel',
      LIST: '/establishment/stock/list',
      ENTRY: '/establishment/stock/entry',
      OUT: '/establishment/stock/out',
    },
    PURCHASE: {
      PAY: '/establishment/purchase/pay',
      PIX_INIT: '/establishment/purchase/pix/init',
      PIX_CONFIRM: '/establishment/purchase/pix/confirm',
      PAYMENT_RECEIPT: '/establishment/purchase/payment/receipt',
    },
    DASHBOARD: {
      GET: '/establishment/dashboard',
    },
    PAYTIME: {
      INFO: '/establishment/paytime/info',
      CREATE: '/establishment/paytime/create',
      SUB_ACTIVATE: '/establishment/paytime/sub/activate',
    },
    TAX: {
      INTEGRATION_STATUS: '/establishment/tax/integration/status',
      CONFIG: '/establishment/tax/config',
      CST_LIST: '/establishment/tax/cst/list',
      PIS_COFINS_LIST: '/establishment/tax/pis-cofins/list',
      NFE_INTEGRATE: '/establishment/nfe/integrate',
      NFE_ACTIVATE: '/establishment/nfe/activate',
      NFE_DELETE: '/establishment/nfe/delete',
      NFE_CERTIFICATE_DELETE: '/establishment/nfe/certificates/delete',
      NFE_CERTIFICATE_UPLOAD: '/establishment/nfe/certificates',
    },
  },
  ORDERS: {
    REPORT: {
      OVERALL: '/orders/report/overall',
      OVERALL_BY_INTERVAL: '/orders/report/overall-by-interval',
      BY_ATTENDANT: '/orders/report/by-attendant',
      PCT_UNDER_TARGET: '/orders/report/pct-under-target',
    },
  },
  TOTEM: {
    BILLING: {
      SUMMARY: '/establishment/totem-billing/summary',
    },
  },
} as const;

class EstablishmentService {
  private readonly baseUrl = 'http://localhost:8086'; // Note: Different port from auth service

  // ==================== ESTABLISHMENT MANAGEMENT ====================

  /**
   * List all establishments
   * GET /establishment/list
   */
  async listEstablishments(params?: EstablishmentSearchParams): Promise<EstablishmentListResponse> {
    try {
      // Apply default parameters
      const requestParams = {
        limit: 20,
        offset: 0,
        ...params,
      };

      // Remove q parameter if it's empty to avoid issues
      if (requestParams.q === '') {
        delete requestParams.q;
      }

      const response = await apiClient.get<EstablishmentListResponse | Establishment[]>(ROUTES.ESTABLISHMENT.LIST, { params: requestParams });

      // Handle both array response and object response
      if (Array.isArray(response)) {
        // Convert simple array response to expected format
        // The API is returning data in this format:
        // [{"id":"...","name":"...","cnpj":"...","address":"...","city":"...","state":"...","created_date":"..."}]
        const establishments = response.map((item) => ({
          id: item.id,
          name: item.name,
          cnpj: item.cnpj || '',
          address: item.address || '',
          zipcode: '',
          city: item.city || '',
          state: item.state || '',
          status: true, // Default to active
          payment_status: false,
          id_institution: '', // Not provided in simple response
          establishment_type_id: '',
          bank_id: '',
          pix_key: '',
          bank_agency: 0,
          bank_account: 0,
          superadmin_user_id: '',
          created_at: item?.created_at || new Date().toISOString(),
          updated_at: item?.updated_at || new Date().toISOString(),
        }));

        const establishmentListResponse: EstablishmentListResponse = {
          establishments,
          total: establishments.length,
          has_more: establishments.length === requestParams.limit,
          offset: requestParams.offset,
        };
        return establishmentListResponse;
      }

      // Handle object response that's already in the correct format
      if (response && typeof response === 'object' && 'establishments' in response) {
        // Calculate has_more if not provided by API
        if (!('has_more' in response)) {
          const totalReceived = response.establishments?.length || 0;
          response.has_more = totalReceived === requestParams.limit;
          response.offset = requestParams.offset;
        }
        return response;
      }

      // Fallback: treat as single establishment and wrap in list format
      return {
        establishments: [],
        total: 0,
        has_more: false,
        offset: requestParams.offset,
      };
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar estabelecimentos');
    }
  }

  /**
   * Get establishment by ID
   * @param establishmentId - The ID of the establishment
   */
  async getEstablishmentById(establishmentId: string): Promise<Establishment | null> {
    try {
      // Note: This would typically be a specific endpoint like /establishment/{id}
      // For now, we'll fetch all and filter (not ideal for production)
      const response = await this.listEstablishments();
      const establishment = response.establishments?.find((est) => est.id === establishmentId);
      return establishment || null;
    } catch (error) {
      console.error('Error getting establishment by ID:', error);
      return null;
    }
  }

  /**
   * Check if establishment exists
   */
  async establishmentExists(establishmentId: string): Promise<boolean> {
    const establishment = await this.getEstablishmentById(establishmentId);
    return establishment !== null;
  }

  /**
   * Get all establishments for dropdown/select options
   * GET /establishment/list/all
   */
  async getAllEstablishments(): Promise<Establishment[]> {
    try {
      const response = await apiClient.get<{
        items: Array<{
          id: string;
          name: string;
          cnpj: string;
          status: string;
          address: string;
          zipcode: string;
          city: string;
          state: string;
          institution_name: string;
          establishment_type: string;
          superadmin_name: string;
          superadmin_email: string;
          superadmin_phone: string;
          support_code: string;
        }>;
        limit: number;
        offset: number;
        has_more: boolean;
        next_offset: number;
      }>(ROUTES.ESTABLISHMENT.LIST_ALL);

      // Handle the response - extract items array and map to Establishment format
      if (response?.items && Array.isArray(response.items)) {
        return response.items.map((item) => ({
          id: item.id,
          name: item.name,
          cnpj: item.cnpj || '',
          address: item.address || '',
          zipcode: item.zipcode || '',
          city: item.city || '',
          state: item.state || '',
          status: item.status === 'true', // Convert string to boolean
          payment_status: false, // Not provided in API response
          id_institution: '', // Not directly provided, would need institution mapping
          establishment_type_id: '', // Not directly provided as ID
          bank_id: '',
          pix_key: '',
          bank_agency: 0,
          bank_account: 0,
          superadmin_user_id: '', // Not provided as ID
          created_at: new Date().toISOString(), // Not provided in response
          updated_at: new Date().toISOString(), // Not provided in response
        }));
      }

      return [];
    } catch (error) {
      console.error('Error getting all establishments:', error);
      return [];
    }
  }

  /**
   * Create a new establishment
   * POST /establishment/create
   */
  async createEstablishment(data: CreateEstablishmentRequest): Promise<Establishment> {
    try {
      console.log('Creating establishment with data:', data);
      console.log('API Route:', ROUTES.ESTABLISHMENT.CREATE);
      const response = await apiClient.post<Establishment>(ROUTES.ESTABLISHMENT.CREATE, data);
      console.log('Establishment created response:', response);
      return response;
    } catch (error) {
      console.error('Error in createEstablishment service:', error);
      throw this.handleError(error, 'Erro ao criar estabelecimento');
    }
  }

  /**
   * Update an existing establishment
   * PUT /establishment/update
   */
  async updateEstablishment(data: UpdateEstablishmentRequest): Promise<Establishment> {
    try {
      return await apiClient.put<Establishment>(ROUTES.ESTABLISHMENT.UPDATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar estabelecimento');
    }
  }

  /**
   * Get establishment types
   * GET /establishment/type
   */
  async listEstablishmentTypes(): Promise<EstablishmentTypeListResponse> {
    try {
      const response = await apiClient.get<EstablishmentType[] | EstablishmentTypeListResponse>(ROUTES.ESTABLISHMENT.TYPE);

      // Handle both array response and object response
      if (Array.isArray(response)) {
        return {
          types: response,
          total: response.length,
        };
      }

      return response;
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar tipos de estabelecimento');
    }
  }

  /**
   * Get banks
   * GET /establishment/bank
   */
  async listBanks(): Promise<BankListResponse> {
    try {
      const response = await apiClient.get<Bank[] | BankListResponse>(ROUTES.ESTABLISHMENT.BANK);

      // Handle both array response and object response
      if (Array.isArray(response)) {
        return {
          banks: response,
          total: response.length,
        };
      }

      return response;
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar bancos');
    }
  }

  /**
   * Get establishments by institution
   * GET /establishment/institution
   */
  async getEstablishmentsByInstitution(institutionId: string): Promise<EstablishmentListResponse> {
    try {
      return await apiClient.get<EstablishmentListResponse>(`${ROUTES.ESTABLISHMENT.INSTITUTION}?ID=${institutionId}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao buscar estabelecimentos da instituição');
    }
  }

  // ==================== USER MANAGEMENT ====================

  /**
   * List users for the authenticated establishment
   * GET /establishment/user/list
   */
  async listEstablishmentUsers(establishmentId: string): Promise<EstablishmentUserListResponse> {
    try {
      return await apiClient.get<EstablishmentUserListResponse>(`${ROUTES.ESTABLISHMENT.USER.LIST}?establishment_id=${establishmentId}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar usuários do estabelecimento');
    }
  }

  /**
   * List all users for specific establishment
   * GET /establishment/user/listall
   */
  async getAllEstablishmentUsers(establishmentId: string): Promise<EstablishmentUserGetAllResponse[]> {
    try {
      return await apiClient.get<EstablishmentUserGetAllResponse[]>(`${ROUTES.ESTABLISHMENT.USER.LIST_ALL}?establishment_id=${establishmentId}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar todos os usuários do estabelecimento');
    }
  }

  /**
   * List all operators for establishment
   * GET /establishment/user/operator/list
   */
  async listAllEstablishmentOperators(establishmentId: string): Promise<EstablishmentOperatorListResponse[]> {
    try {
      return await apiClient.get<EstablishmentOperatorListResponse[]>(
        `${ROUTES.ESTABLISHMENT.USER.OPERATOR_LIST}?establishment_id=${establishmentId}`,
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar todos os operadores do estabelecimento');
    }
  }

  /**
   * Create establishment user
   * POST /establishment/user/create
   */
  async createEstablishmentInternalUser(data: CreateEstablishmentInternalUserRequest): Promise<CreateEstablishmentInternalUserResponse> {
    try {
      return await apiClient.post<CreateEstablishmentInternalUserResponse>(ROUTES.ESTABLISHMENT.USER.CREATE, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar usuário do estabelecimento');
    }
  }

  /**
   * Check if internal user exists by CPF
   * GET /user/internal/check?cpf=CPF
   */
  async checkInternalUserByCpf(cpf: string): Promise<InternalUserCheckResponse> {
    try {
      const cleanCpf = cpf.replace(/\D/g, '');
      return await apiClient.get<InternalUserCheckResponse>(ROUTES.ESTABLISHMENT.USER.CHECK, {
        params: { cpf: cleanCpf },
      });
    } catch (error) {
      throw this.handleError(error, 'Erro ao verificar CPF');
    }
  }

  /**
   * Add internal user to establishment (membership)
   * POST /user/internal/membership
   */
  async addInternalMembership(data: InternalMembershipRequest): Promise<{ success?: boolean }> {
    try {
      return await apiClient.post<{ success?: boolean }>(ROUTES.ESTABLISHMENT.USER.MEMBERSHIP, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao incluir usuário no estabelecimento');
    }
  }

  /**
   * Edit establishment user
   * PUT /establishment/user/edit
   */
  async editEstablishmentUser(data: UpdateEstablishmentUserRequest): Promise<EstablishmentUser> {
    try {
      return await apiClient.put<EstablishmentUser>(ROUTES.ESTABLISHMENT.USER.EDIT, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao editar usuário do estabelecimento');
    }
  }

  /**
   * Delete establishment user
   * POST /establishment/user/delete
   */
  async deleteEstablishmentUser(data: DeleteEstablishmentUserRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.post<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.USER.DELETE, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao deletar usuário do estabelecimento');
    }
  }

  // ==================== INTERVAL MANAGEMENT ====================

  /**
   * Create a new interval
   * POST /establishment/interval/create
   */
  async createInterval(data: CreateIntervalRequest): Promise<EstablishmentInterval> {
    try {
      return await apiClient.post<EstablishmentInterval>(ROUTES.ESTABLISHMENT.INTERVAL.CREATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar intervalo');
    }
  }

  /**
   * Update an existing interval
   * POST /establishment/interval/update
   */
  async updateInterval(data: UpdateIntervalRequest): Promise<EstablishmentInterval> {
    try {
      return await apiClient.post<EstablishmentInterval>(ROUTES.ESTABLISHMENT.INTERVAL.UPDATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar intervalo');
    }
  }

  async deleteInterval(id: string): Promise<{ message: string }> {
    try {
      return await apiClient.post<{ message: string }>(`${ROUTES.ESTABLISHMENT.INTERVAL.DELETE}/${id}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao deletar intervalo');
    }
  }

  /**
   * List intervals for establishment
   * GET /establishment/interval/list/{establishment_id}
   */
  async listIntervals(establishmentId: string): Promise<IntervalListResponse> {
    try {
      return await apiClient.get<IntervalListResponse>(`${ROUTES.ESTABLISHMENT.INTERVAL.LIST}/${establishmentId}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar intervalos');
    }
  }

  // ==================== CLASS MANAGEMENT ====================

  /**
   * Create a new class
   * POST /establishment/turma/create
   */
  async createClass(data: CreateClassRequest): Promise<EstablishmentClass> {
    try {
      return await apiClient.post<EstablishmentClass>(ROUTES.ESTABLISHMENT.TURMA.CREATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar turma');
    }
  }

  /**
   * Update class association with establishment
   * PUT /establishment/turma/update
   */
  async updateClass(data: UpdateClassRequest): Promise<EstablishmentClass> {
    try {
      return await apiClient.put<EstablishmentClass>(ROUTES.ESTABLISHMENT.TURMA.UPDATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar turma');
    }
  }

  /**
   * List classes for establishment
   * GET /establishment/turma/list
   */
  async listClasses(establishmentId: string): Promise<ClassListResponse> {
    try {
      return await apiClient.get<ClassListResponse>(`${ROUTES.ESTABLISHMENT.TURMA.LIST}?establishment_id=${establishmentId}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar turmas');
    }
  }

  // ==================== MENU MANAGEMENT ====================

  /**
   * Create a new menu
   * POST /establishment/menu/create
   */
  async createMenu(data: CreateMenuRequest): Promise<EstablishmentMenu> {
    try {
      return await apiClient.post<EstablishmentMenu>(ROUTES.ESTABLISHMENT.MENU.CREATE, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar menu');
    }
  }

  /**
   * Upload menu photo
   * POST /establishment/menu/{id}/photo
   */
  async uploadMenuPhoto(menuId: string, data: MenuPhotoRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.post<{ success: boolean; message: string }>(`/establishment/menu/${menuId}/photo`, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao fazer upload da foto do menu');
    }
  }

  /**
   * List menus for establishment
   * GET /establishment/menu/list
   */
  async listMenusBackoffice(params: MenuListParams): Promise<MenuListResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (params.establishment_id) {
        queryParams.append('establishment_id', params.establishment_id);
      }

      if (params.menuID) {
        queryParams.append('menuID', params.menuID);
      }

      if (params.class_id) {
        queryParams.append('class_id', params.class_id);
      }

      if (params.q) {
        queryParams.append('q', params.q);
      }

      return await apiClient.get<MenuListResponse>(`${ROUTES.ESTABLISHMENT.MENU.BACKOFFICE_LIST}?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar menus');
    }
  }

  /**
   * Update menu
   * PUT /establishment/menu/update
   */
  async updateMenu(data: UpdateMenuRequest): Promise<EstablishmentMenu> {
    try {
      return await apiClient.put<EstablishmentMenu>(ROUTES.ESTABLISHMENT.MENU.UPDATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar menu');
    }
  }

  /**
   * Attach menu to establishment
   * PUT /backoffice/establishment/menu-attach
   */
  async attachMenu(data: AttachMenuRequest): Promise<{ success: boolean; message: string }> {
    try {
      const dataToSend = {
        establishment_id: data.establishment_id,
        menu_id: data.menu_id,
        visible: data.visible,
      };
      return await apiClient.put<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.MENU.ATTACH, dataToSend, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao associar cardápio ao estabelecimento');
    }
  }

  /**
   * Detach menu from establishment
   * DELETE /backoffice/establishment/menu-detach
   */
  async detachMenu(data: DetachMenuRequest): Promise<{ success: boolean; message: string }> {
    try {
      const dataToSend = {
        establishment_id: data.establishment_id,
        menu_id: data.menu_id,
      };
      return await apiClient.delete<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.MENU.DETACH, { data: dataToSend, skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao remover cardápio do estabelecimento');
    }
  }

  // ==================== PRODUCT MANAGEMENT ====================

  /**
   * Create a new product
   * POST /establishment/product/create
   */
  async createProduct(data: CreateEstablishmentProductRequest): Promise<EstablishmentProduct> {
    try {
      return await apiClient.post<EstablishmentProduct>(ROUTES.ESTABLISHMENT.PRODUCT.CREATE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar produto');
    }
  }

  /**
   * List products for establishment
   * GET /establishment/product/list
   */
  async listProducts(params: EstablishmentProductSearchParams): Promise<EstablishmentProductListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('establishment_id', params.establishment_id);

      if (params.menu_id) {
        queryParams.append('menu_id', params.menu_id);
      }

      if (params.q) {
        queryParams.append('q', params.q);
      }

      if (params.include_details !== undefined) {
        queryParams.append('include_details', String(params.include_details));
      }

      return await apiClient.get<EstablishmentProductListResponse>(`${ROUTES.ESTABLISHMENT.PRODUCT.LIST}?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar produtos');
    }
  }

  /**
   * List all products
   * GET /establishment/product/list-all
   */
  async listAllProducts(): Promise<ProductListAllResponse> {
    try {
      return await apiClient.get<ProductListAllResponse>(`${ROUTES.ESTABLISHMENT.PRODUCT.LIST_ALL}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar produtos');
    }
  }

  /**
   * Get product details
   * GET /establishment/product/detail
   */
  async getProductDetail(params: EstabilishmentProductDetailsParams): Promise<EstablishmentProductDetailResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('establishment_id', params.establishment_id);
      queryParams.append('product_id', params.product_id);
      return await apiClient.get<EstablishmentProductDetailResponse>(`${ROUTES.ESTABLISHMENT.PRODUCT.DETAIL}?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter detalhes do produto');
    }
  }

  /**
   * Update product
   * PUT /establishment/product/update
   */
  async updateProduct(data: UpdateEstablishmentProductRequest, params: UpdateEstablishmentProductRequestParams): Promise<EstablishmentProduct> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('establishment_id', params.establishment_id);
      queryParams.append('product_id', params.product_id);
      return await apiClient.patch<EstablishmentProduct>(`${ROUTES.ESTABLISHMENT.PRODUCT.UPDATE}?${queryParams.toString()}`, data, {
        skipToast: true,
      });
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar produto');
    }
  }

  /**
   * Update product status
   * PUT /establishment/product/update/status
   */
  async updateProductStatus(data: UpdateEstablishmentProductStatusRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.put<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.PRODUCT.UPDATE_STATUS, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar status do produto');
    }
  }

  /**
   * Delete class price for a product
   * DELETE /establishment/product/del/class-price
   */
  async deleteClassPrice(data: { product_id: string; establishment_id: string; class_id: string }): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiClient.delete<{ success: boolean; message?: string }>(ROUTES.ESTABLISHMENT.PRODUCT.DEL_CLASS_PRICE, {
        data,
        skipToast: true,
      });
    } catch (error) {
      throw this.handleError(error, 'Erro ao remover preço por turma');
    }
  }

  /**
   * Delete blocked profile for a product
   * DELETE /establishment/product/del/profile-blocked
   */
  async deleteBlockedProfile(data: {
    product_id: string;
    establishment_id: string;
    profile_id: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiClient.delete<{ success: boolean; message?: string }>(ROUTES.ESTABLISHMENT.PRODUCT.DEL_PROFILE_BLOCKED, {
        data,
        skipToast: true,
      });
    } catch (error) {
      throw this.handleError(error, 'Erro ao remover perfil bloqueado');
    }
  }

  /**
   * Delete blocked class for a product
   * DELETE /establishment/product/class-blocked
   */
  async deleteBlockedClass(data: {
    product_id: string;
    establishment_id: string;
    class_id: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiClient.delete<{ success: boolean; message?: string }>(ROUTES.ESTABLISHMENT.PRODUCT.DEL_CLASS_BLOCKED, {
        data,
        skipToast: true,
      });
    } catch (error) {
      throw this.handleError(error, 'Erro ao remover classe bloqueada');
    }
  }

  /**
   * Get global product suggestions
   * GET /establishment/product/global-suggestions
   */
  async getProductSuggestions(params: GlobalProductSearchParams): Promise<EstablishmentProductSuggestionsResponse> {
    try {
      return await apiClient.get<EstablishmentProductSuggestionsResponse>(
        `/establishment/product/global-suggestions?query=${encodeURIComponent(params.query)}`,
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter sugestões de produtos');
    }
  }

  /**
   * Get global product list
   * GET /establishment/product/global-list
   */
  async getGlobalProductList(): Promise<EstablishmentProductListResponse> {
    try {
      return await apiClient.get<EstablishmentProductListResponse>(ROUTES.ESTABLISHMENT.PRODUCT.GLOBAL_LIST);
    } catch (error) {
      throw this.handleError(error, 'Erro ao obter lista global de produtos');
    }
  }

  /**
   * Link multiple products to establishment in a single call
   * POST /establishment/product/add-batch
   * Partial failure: the response reports item by item what succeeded and what failed.
   */
  async linkProductsBatch(data: LinkProductsBatchRequest): Promise<LinkProductsBatchResponse> {
    try {
      return await apiClient.post<LinkProductsBatchResponse>(ROUTES.ESTABLISHMENT.PRODUCT.LINK_BATCH, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao vincular produtos em lote');
    }
  }

  // ==================== STOCK MANAGEMENT ====================

  /**
   * Add stock
   * POST /establishment/stock/add
   */
  async addStock(data: AddStockRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.post<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.STOCK.ADD, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao adicionar estoque');
    }
  }

  /**
   * Schedule stock transfer — um ou vários produtos, numa direção só.
   * POST /establishment/stock/schedule
   *
   * Cria uma transação pendente por produto; o job as executa quando
   * transaction_date chega. Falha por estoque insuficiente volta em 422 com a
   * lista completa dos itens que reprovaram (StockTransferValidationError).
   */
  async scheduleStockTransfer(data: ScheduleStockTransferRequest): Promise<ScheduleStockTransferResponse> {
    try {
      return await apiClient.post<ScheduleStockTransferResponse>(ROUTES.ESTABLISHMENT.STOCK.SCHEDULE, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao agendar transferência de estoque');
    }
  }

  /**
   * Stock transfer — um ou vários produtos, numa direção só.
   * POST /establishment/stock/transfers
   *
   * Tudo-ou-nada: se algum item não tiver estoque, nada é movimentado e o 422
   * traz todas as faltas de uma vez (StockTransferValidationError).
   */
  async stockTransfer(data: StockTransferRequest): Promise<StockTransferResponse> {
    try {
      return await apiClient.post<StockTransferResponse>(ROUTES.ESTABLISHMENT.STOCK.TRANSFERS, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao transferir estoque');
    }
  }

  /**
   * List stock transfers
   * GET /establishment/stock/transfers
   */
  async listStockTransfers(params: StockTransferParams): Promise<StockTransferListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('establishment_id', params.establishment_id);
      queryParams.append('status', params.status);

      return await apiClient.get<StockTransferListResponse>(`/establishment/stock/transfers?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar transferências de estoque');
    }
  }

  /**
   * Execute stock transfer
   * PUT /establishment/stock/execute
   */
  async executeStockTransfer(data: ExecuteStockTransferRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.put<{ success: boolean; message: string }>(ROUTES.ESTABLISHMENT.STOCK.EXECUTE, data);
    } catch (error) {
      throw this.handleError(error, 'Erro ao executar transferência de estoque');
    }
  }

  /**
   * Cancel stock transfer
   * POST /establishment/stock/cancel?transaction_id={transaction_id}
   */
  async cancelStockTransfer(data: CancelStockTransferRequest): Promise<{ success: boolean; message: string }> {
    try {
      return await apiClient.post<{ success: boolean; message: string }>(
        `${ROUTES.ESTABLISHMENT.STOCK.CANCEL}?transaction_id=${data.transaction_id}`,
        {},
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao cancelar transferência de estoque');
    }
  }

  /**
   * List stock items
   * GET /establishment/stock/list
   */
  async listStock(params: StockListParams): Promise<StockListResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('establishment_id', params.establishment_id);
      queryParams.append('stock', params.stock);
      if (params.q) queryParams.append('q', params.q);
      if (params.supplier_id) queryParams.append('supplier_id', params.supplier_id);
      if (params.payment_status) queryParams.append('payment_status', params.payment_status);
      if (params.nf_number) queryParams.append('nf_number', params.nf_number);
      if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
      if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

      return await apiClient.get<StockListResponse>(`/establishment/stock/list?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar estoque');
    }
  }

  /**
   * List stock summary
   * GET /establishment/stock/summary
   */
  async listStockSummary(params: StockSummaryParams): Promise<StockSummaryResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('establishment_id', params.establishment_id);

    try {
      return await apiClient.get<StockSummaryResponse>(`/establishment/stock/sumary?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar resumo de estoque');
    }
  }

  /**
   * List stock movements
   * GET /establishment/stock/movements
   */
  async listStockMovements(params: StockMovementsParams): Promise<StockMovementsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('establishment_id', params.establishment_id);

    if (params.product_id) queryParams.append('product_id', params.product_id);
    if (params.status && params.status !== 'all') queryParams.append('status', params.status);
    if (params.type && params.type !== 'all') queryParams.append('type', params.type);
    if (params.q) queryParams.append('q', params.q);
    if (params.date_from) queryParams.append('date_from', params.date_from);
    if (params.date_to) queryParams.append('date_to', params.date_to);
    if (params.supplier_id) queryParams.append('supplier_id', params.supplier_id);
    if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
    if (params.nf_number) queryParams.append('nf_number', params.nf_number);
    if (params.due_from) queryParams.append('due_from', params.due_from);
    if (params.due_to) queryParams.append('due_to', params.due_to);
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.offset !== undefined) queryParams.append('offset', params.offset.toString());

    try {
      return await apiClient.get<StockMovementsResponse>(`/establishment/stock/movimentacao?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar movimentações de estoque');
    }
  }

  /**
   * List stock entry
   * GET /establishment/stock/entry
   */
  async createStockEntry(body: CreateStockEntryRequest): Promise<CreateStockEntryResponse> {
    const { establishment_id, ...payload } = body;
    return await apiClient.post<CreateStockEntryResponse>(
      `/establishment/stock/entry?establishment_id=${establishment_id}`,
      payload,
      { skipToast: true },
    );
  }

  /**
   * List stock out
   * GET /establishment/stock/out
   */
  async createStockOut(body: CreateStockOutRequest): Promise<CreateStockOutResponse> {
    return await apiClient.post<CreateStockOutResponse>(`/establishment/stock/out`, body, { skipToast: true });
  }

  async listStockNFs(params: StockNFsParams): Promise<StockNFsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('establishment_id', params.establishment_id);
    if (params.source) {
      queryParams.append('source', params.source);
    }
    queryParams.append('product_id', params.product_id);

    return await apiClient.get<StockNFsResponse>(`/establishment/stock/nf?${queryParams.toString()}`);
  }

  /**
   * List stock expiration
   * GET /establishment/stock/expiration
   */
  async listStockExpiration(params: StockExpirationParams): Promise<StockExpirationResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('establishment_id', params.establishment_id);
    queryParams.append('status', params.status);

    try {
      return await apiClient.get<StockExpirationResponse>(`/establishment/stock/expiration?${queryParams.toString()}`);
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar produtos expirando');
    }
  }

  // ==================== SUPPLIER MANAGEMENT ====================

  async listSuppliers(params: SupplierListParams): Promise<SupplierListResponse> {
    const qp = new URLSearchParams();
    qp.append('establishment_id', params.establishment_id);
    if (params.active) qp.append('active', params.active);
    if (params.q) qp.append('q', params.q);
    if (params.limit !== undefined) qp.append('limit', params.limit.toString());
    if (params.offset !== undefined) qp.append('offset', params.offset.toString());
    try {
      return await apiClient.get<SupplierListResponse>(`${ROUTES.ESTABLISHMENT.SUPPLIER.LIST}?${qp.toString()}`, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao listar fornecedores');
    }
  }

  async getSupplier(establishment_id: string, id: string): Promise<Supplier> {
    try {
      return await apiClient.get<Supplier>(`${ROUTES.ESTABLISHMENT.SUPPLIER.GET}?establishment_id=${establishment_id}&id=${id}`, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao buscar fornecedor');
    }
  }

  async createSupplier(establishment_id: string, data: CreateSupplierRequest): Promise<Supplier> {
    try {
      return await apiClient.post<Supplier>(`${ROUTES.ESTABLISHMENT.SUPPLIER.CREATE}?establishment_id=${establishment_id}`, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao criar fornecedor');
    }
  }

  async updateSupplier(establishment_id: string, data: UpdateSupplierRequest): Promise<Supplier> {
    try {
      return await apiClient.post<Supplier>(`${ROUTES.ESTABLISHMENT.SUPPLIER.UPDATE}?establishment_id=${establishment_id}`, data, { skipToast: true });
    } catch (error) {
      throw this.handleError(error, 'Erro ao atualizar fornecedor');
    }
  }

  async deleteSupplier(establishment_id: string, data: DeleteSupplierRequest): Promise<DeleteSupplierResponse> {
    try {
      return await apiClient.post<DeleteSupplierResponse>(
        `${ROUTES.ESTABLISHMENT.SUPPLIER.DELETE}?establishment_id=${establishment_id}`,
        data,
        { skipToast: true },
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao inativar fornecedor');
    }
  }

  // ==================== PURCHASE PAYMENT ====================

  async payPurchase(establishment_id: string, data: PayPurchaseRequest): Promise<PayPurchaseResponse> {
    try {
      return await apiClient.post<PayPurchaseResponse>(
        `${ROUTES.ESTABLISHMENT.PURCHASE.PAY}?establishment_id=${establishment_id}`,
        data,
        { skipToast: true },
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao registrar pagamento');
    }
  }

  async purchasePixInit(establishment_id: string, data: PurchasePixInitRequest): Promise<PurchasePixInitResponse> {
    try {
      return await apiClient.post<PurchasePixInitResponse>(
        `${ROUTES.ESTABLISHMENT.PURCHASE.PIX_INIT}?establishment_id=${establishment_id}`,
        data,
        { skipToast: true },
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao iniciar pagamento Pix');
    }
  }

  async purchasePixConfirm(establishment_id: string, data: PurchasePixConfirmRequest): Promise<PurchasePixConfirmResponse> {
    try {
      return await apiClient.post<PurchasePixConfirmResponse>(
        `${ROUTES.ESTABLISHMENT.PURCHASE.PIX_CONFIRM}?establishment_id=${establishment_id}`,
        data,
        { skipToast: true },
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao confirmar pagamento Pix');
    }
  }

  async purchasePaymentReceipt(establishment_id: string, payment_id: string): Promise<PaymentReceipt> {
    try {
      return await apiClient.get<PaymentReceipt>(
        `${ROUTES.ESTABLISHMENT.PURCHASE.PAYMENT_RECEIPT}?establishment_id=${establishment_id}&payment_id=${payment_id}`,
        { skipToast: true } as any,
      );
    } catch (error) {
      throw this.handleError(error, 'Erro ao buscar comprovante');
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle establishment-specific errors
   */
  private handleError(error: unknown, defaultMessage: string): ApiError {
    if (error && typeof error === 'object' && 'message' in error) {
      const apiError = error as ApiError;

      // Preserve backend message when available, fall back to generic messages
      return {
        ...apiError,
        message: apiError.message || defaultMessage,
      };
    }

    return {
      name: 'EstablishmentError',
      message: defaultMessage,
      status: 0,
    };
  }

  // ==================== CONVENIENCE METHODS ====================

  /**
   * List all users for a specific establishment
   * This method supports pagination and search parameters
   */
  async listAllEstablishmentUsers(
    establishmentId: string,
    params?: import('./establishmentTypes').EstablishmentUserSearchParams,
  ): Promise<import('./establishmentTypes').EstablishmentUser[]> {
    try {
      // Apply default parameters for new pagination pattern
      const requestParams = {
        limit: 20,
        offset: 0,
        q: '',
        ...params,
      };

      // Build API endpoint URL with query parameters
      let url = `/establishment/${establishmentId}/users`;
      const queryParams = new URLSearchParams();

      // Add pagination and search parameters
      if (requestParams.q) queryParams.append('q', requestParams.q);
      if (requestParams.limit) queryParams.append('limit', requestParams.limit.toString());
      if (requestParams.offset) queryParams.append('offset', requestParams.offset.toString());

      // Add specific filters
      if (requestParams.first_name) queryParams.append('first_name', requestParams.first_name);
      if (requestParams.last_name) queryParams.append('last_name', requestParams.last_name);
      if (requestParams.email) queryParams.append('email', requestParams.email);
      if (requestParams.role) queryParams.append('role', requestParams.role);
      if (requestParams.status !== undefined) queryParams.append('status', requestParams.status.toString());

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      // For now, return mock data that simulates the API response structure
      // TODO: Replace with actual API call when endpoint is available
      const mockUsers: import('./establishmentTypes').EstablishmentUser[] = [
        {
          id: '1',
          first_name: 'João',
          last_name: 'Silva',
          email: 'joao.silva@example.com',
          phone: '11999999999',
          role: 'manager',
          establishment_id: establishmentId,
        },
        {
          id: '2',
          first_name: 'Maria',
          last_name: 'Santos',
          email: 'maria.santos@example.com',
          phone: '11888888888',
          role: 'cashier',
          establishment_id: establishmentId,
        },
      ];

      // Apply search filtering if there's a query
      if (requestParams.q) {
        const searchTerm = requestParams.q.toLowerCase();
        return mockUsers.filter(
          (user) =>
            user.first_name.toLowerCase().includes(searchTerm) ||
            user.last_name.toLowerCase().includes(searchTerm) ||
            user.email.toLowerCase().includes(searchTerm) ||
            user.role.toLowerCase().includes(searchTerm),
        );
      }

      return mockUsers;
    } catch (error) {
      console.error('Error listing establishment users:', error);
      throw error;
    }
  }

  /**
   * Get establishment by ID from list
   */
  // async getEstablishmentById(establishmentId: string): Promise<Establishment | null> {
  //   try {
  //     const response = await this.listEstablishments();
  //     const establishment = response?.find((est) => est.id === establishmentId);
  //     return establishment || null;
  //   } catch (error) {
  //     console.error('Error getting establishment by ID:', error);
  //     return null;
  //   }
  // }

  /**
   * Get all menus and products for establishment
   */
  async getEstablishmentMenusWithProducts(establishmentId: string): Promise<MenuListResponse> {
    return this.listMenusBackoffice({ establishment_id: establishmentId });
  }

  /**
   * Get products by menu ID
   */
  async getProductsByMenu(establishmentId: string, menuId: string): Promise<MenuListResponse> {
    return this.listMenusBackoffice({
      establishment_id: establishmentId,
      menuID: menuId,
    });
  }

  /**
   * Get products with class-specific pricing
   */
  async getProductsWithClassPricing(establishmentId: string, menuId: string, classId: string): Promise<MenuListResponse> {
    return this.listMenusBackoffice({
      establishment_id: establishmentId,
      menuID: menuId,
      class_id: classId,
    });
  }

  getDashboardData(params: GetDashboardDataParams): Promise<GetDashboardDataResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    if (params.period) {
      urlParams.append('period', params.period);
    } else {
      urlParams.append('period', 'day');
    }
    if (params.date && params.period === 'month') {
      urlParams.append('date', params.date);
    }

    return apiClient.get<GetDashboardDataResponse>(`${ROUTES.ESTABLISHMENT.DASHBOARD.GET}?${urlParams.toString()}`);
  }

  /**
   * Get establishment balance
   * GET /establishment/balance
   */
  async getEstablishmentBalance(): Promise<EstablishmentBalance> {
    const urlParams = new URLSearchParams();
    const currentEstablishment = authService.getCurrentEstablishment();
    if (!currentEstablishment?.id) {
      throw new Error('Establishment ID is required');
    }
    urlParams.append('establishment_id', currentEstablishment.id);
    return apiClient.get<EstablishmentBalance>(`${ROUTES.ESTABLISHMENT.BALANCE.GET}?${urlParams.toString()}`);
  }

  // ==================== SALES METHODS ====================
  /**
   * Get establishment sales
   * GET /establishment/sales
   */
  async getEstablishmentSales(params: GetEstablishmentSalesQuery): Promise<GetEstablishmentSalesResponse> {
    const urlParams = new URLSearchParams();

    urlParams.append('establishment_id', params.establishment_id);

    if (params.period) {
      urlParams.append('period', params.period);
    }
    if (params.paymentstatus && params.paymentstatus !== 'all') {
      urlParams.append('paymentstatus', params.paymentstatus);
    }
    if (params.channel && params.channel !== 'all') {
      urlParams.append('channel', params.channel);
    }
    if (params.method && params.method !== 'all') {
      urlParams.append('method', params.method);
    }
    if (params.q) {
      urlParams.append('q', params.q);
    }
    // Só adiciona o parâmetro date se o período for 'month'
    if (params.date && params.period === 'month') {
      const newDate = params.date.split('-');
      urlParams.append('date', `${newDate[1]}-${newDate[0]}`);
    }

    return apiClient.get<GetEstablishmentSalesResponse>(`${ROUTES.ESTABLISHMENT.SALES.GET}?${urlParams.toString()}`);
  }

  // ==================== LUNCHBOX METHODS ====================
  /**
   * Get establishment lunchbox
   * GET /establishment/lunchbox
   */
  async getEstablishmentLunchboxDashboard(params: EstablishmentLunchboxDashboardParams): Promise<EstablishmentLunchboxDashboard> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id || '');
    if (params.date) {
      urlParams.append('date', params.date);
    } else if (params.period) {
      urlParams.append('period', params.period);
    } else {
      urlParams.append('period', 'day');
    }
    if (params.interval_id && params.interval_id !== 'all') {
      urlParams.append('interval_id', params.interval_id);
    }

    const dashboard = await apiClient.get<EstablishmentLunchboxDashboard>(`${ROUTES.ESTABLISHMENT.LUNCHBOX.GET_DASHBOARD}?${urlParams.toString()}`);

    return {
      ...dashboard,
      by_class: Array.isArray(dashboard.by_class) ? dashboard.by_class : [],
      by_product: Array.isArray(dashboard.by_product) ? dashboard.by_product : [],
      by_student: Array.isArray(dashboard.by_student) ? dashboard.by_student : [],
    };
  }

  /**
   * Get lunchbox class detail (products summary + students breakdown)
   * GET /lunchbox/class/detail
   */
  async getEstablishmentLunchboxClassDetail(params: EstablishmentLunchboxClassDetailParams): Promise<EstablishmentLunchboxClassDetail> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    urlParams.append('interval_id', params.interval_id);
    urlParams.append('class_id', params.class_id);
    urlParams.append('date', params.date);

    const detail = await apiClient.get<EstablishmentLunchboxClassDetail>(
      `${ROUTES.ESTABLISHMENT.LUNCHBOX.GET_CLASS_DETAIL}?${urlParams.toString()}`,
    );

    return {
      ...detail,
      products: Array.isArray(detail.products) ? detail.products : [],
      students: Array.isArray(detail.students) ? detail.students : [],
    };
  }

  /**
   * Get establishment extract
   * GET /establishment/extrato
   */
  async getEstablishmentExtract(params: GetEstablishmentExtractParams): Promise<GetEstablishmentExtractResponse> {
    const urlParams = new URLSearchParams();
    const currentEstablishment = authService.getCurrentEstablishment();
    urlParams.append('establishment_id', currentEstablishment?.id as string);
    if (params.date) {
      // Extrair apenas ano e mês da data no formato YYYY-MM-DD
      const yearMonth = params.date.substring(0, 7); // Pega "2025-10" de "2025-10-01"
      urlParams.append('month', yearMonth);
    }

    return apiClient.get<GetEstablishmentExtractResponse>(`${ROUTES.ESTABLISHMENT.EXTRACT}?${urlParams.toString()}`);
  }

  /**
   * Get establishment withdraws history
   * GET /establishment/withdraws/history
   */
  async getEstablishmentWithdrawsHistory(params: GetEstablishmentWithdrawsHistoryParams): Promise<GetEstablishmentWithdrawsHistoryResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    if (params.from) {
      urlParams.append('from', params.from);
    }
    if (params.to) {
      urlParams.append('to', params.to);
    }
    if (params.period && !params.to && !params.from) {
      urlParams.append('period', params.period);
    }
    if (params.page) {
      urlParams.append('page', params.page.toString());
    }
    if (params.page_size) {
      urlParams.append('page_size', params.page_size.toString());
    }

    return apiClient.get<GetEstablishmentWithdrawsHistoryResponse>(`${ROUTES.ESTABLISHMENT.WITHDRAWS.HISTORY}?${urlParams.toString()}`);
  }

  // ==================== CASH FLOW METHODS ====================
  /**
   * Get establishment cash flow
   * GET /establishment/cash-flow
   */
  async getEstablishmentCashFlowSummary(params: GetEstablishmentCashFlowSummaryParams): Promise<GetEstablishmentCashFlowSummaryResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    if (params.period) {
      urlParams.append('period', params.period);
    }
    if (params.from) {
      urlParams.append('from', params.from);
    }
    if (params.to) {
      urlParams.append('to', params.to);
    }
    if (params.operator_id && params.operator_id !== 'all') {
      urlParams.append('operator_id', params.operator_id);
    }

    return apiClient.get<GetEstablishmentCashFlowSummaryResponse>(`${ROUTES.ESTABLISHMENT.CASH_FLOW.SUMMARY}?${urlParams.toString()}`);
  }

  /**
   * Get establishment cash flow movements
   * GET /establishment/pdv/reports/movements
   */
  async getEstablishmentCashFlowMovements(params: GetEstablishmentCashFlowMovementsParams): Promise<GetEstablishmentCashFlowMovementsResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    if (params.period) {
      urlParams.append('period', params.period);
    }
    if (params.from) {
      urlParams.append('from', params.from);
    }
    if (params.to) {
      urlParams.append('to', params.to);
      urlParams.append('period', 'month');
    }
    if (params.operator_id && params.operator_id !== 'all') {
      urlParams.append('operator_id', params.operator_id);
    }

    if (!params.period)
      return Promise.resolve({
        items: [],
        total: 0,
        page: 1,
        page_size: 10,
      });
    return apiClient.get<GetEstablishmentCashFlowMovementsResponse>(`${ROUTES.ESTABLISHMENT.CASH_FLOW.SUMMARY}?${urlParams.toString()}`);
  }

  async postEstablishmentWithdraws(body: PostEstablishmentWithdrawsDto): Promise<PostEstablishmentWithdrawsResponse> {
    return apiClient.post<PostEstablishmentWithdrawsResponse>(`${ROUTES.ESTABLISHMENT.WITHDRAWS.POST}`, body);
  }

  async postWithdrawInit({ establishment_id, ...body }: WithdrawInitDto): Promise<WithdrawInitResponse> {
    return apiClient.post<WithdrawInitResponse>(ROUTES.ESTABLISHMENT.WITHDRAWS.INIT, body, {
      headers: { 'X-Establishment-Id': establishment_id },
      skipToast: true,
    });
  }

  async postWithdrawConfirm({ establishment_id, ...body }: WithdrawConfirmDto): Promise<WithdrawConfirmResponse> {
    return apiClient.post<WithdrawConfirmResponse>(ROUTES.ESTABLISHMENT.WITHDRAWS.CONFIRM, body, {
      headers: { 'X-Establishment-Id': establishment_id },
      skipToast: true,
    });
  }

  async postWithdrawDelete({ establishment_id, ...body }: WithdrawDeleteDto): Promise<WithdrawDeleteResponse> {
    return apiClient.post<WithdrawDeleteResponse>(ROUTES.ESTABLISHMENT.WITHDRAWS.DELETE, body, {
      headers: { 'X-Establishment-Id': establishment_id },
      skipToast: true,
    });
  }

  /**
   * Get orders report overall
   * GET /orders/report/overall
   */
  async getOrdersReportOverall(params: GetOrdersReportOverallParams): Promise<GetOrdersReportOverallResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    urlParams.append('date', params.date);

    return apiClient.get<GetOrdersReportOverallResponse>(`${ROUTES.ORDERS.REPORT.OVERALL}?${urlParams.toString()}`);
  }

  /**
   * Get orders report overall by interval
   * GET /orders/report/overall-by-interval
   */
  async getOrdersReportOverallByInterval(params: GetOrdersReportOverallByIntervalParams): Promise<GetOrdersReportOverallByIntervalResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    urlParams.append('date', params.date);

    return apiClient.get<GetOrdersReportOverallByIntervalResponse>(`${ROUTES.ORDERS.REPORT.OVERALL_BY_INTERVAL}?${urlParams.toString()}`);
  }

  /**
   * Get orders report by attendant
   * GET /orders/report/by-attendant
   */
  async getOrdersReportByAttendant(params: GetOrdersReportByAttendantParams): Promise<GetOrdersReportByAttendantResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    urlParams.append('date', params.date);

    return apiClient.get<GetOrdersReportByAttendantResponse>(`${ROUTES.ORDERS.REPORT.BY_ATTENDANT}?${urlParams.toString()}`);
  }

  /**
   * Get orders report percentage under target
   * GET /orders/report/pct-under-target
   */
  async getOrdersReportPctUnderTarget(params: GetOrdersReportPctUnderTargetParams): Promise<GetOrdersReportPctUnderTargetResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', params.establishment_id);
    urlParams.append('tempo', params.tempo.toString());
    urlParams.append('date', params.date);

    return apiClient.get<GetOrdersReportPctUnderTargetResponse>(`${ROUTES.ORDERS.REPORT.PCT_UNDER_TARGET}?${urlParams.toString()}`);
  }

  /**
   * Get establishment totem billing summary
   * GET /establishment/totem-billing/summary
   *
   * - Ano atual: apenas establishment_id (retorna apenas meses já corridos)
   * - Outros anos: establishment_id + year=YYYY
   */
  async getEstablishmentTotemBillingSummary(year?: string): Promise<EstablishmentTotemBillingSummary> {
    const currentEstablishment = authService.getCurrentEstablishment();
    if (!currentEstablishment?.id) {
      throw new Error('Establishment ID is required');
    }

    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', currentEstablishment.id);
    if (year) {
      urlParams.append('year', year);
    }

    return apiClient.get<EstablishmentTotemBillingSummary>(`${ROUTES.TOTEM.BILLING.SUMMARY}?${urlParams.toString()}`);
  }

  /**
   * Create establishment from admin
   * POST /establishment/create
   */
  async createEstablishmentFromAdmin(data: CreateEstablishmentFromAdminRequest): Promise<Establishment> {
    return apiClient.post<Establishment>(`${ROUTES.ESTABLISHMENT.CREATE}`, data);
  }

  // ==================== PAYTIME (CONTA DIGITAL) ====================

  /**
   * Get Paytime info (conta digital)
   * GET /establishment/paytime/info?establishment_id=UUID
   */
  async getPaytimeInfo(establishmentId: string): Promise<PaytimeInfo> {
    return apiClient.get<PaytimeInfo>(`${ROUTES.ESTABLISHMENT.PAYTIME.INFO}?establishment_id=${establishmentId}`);
  }

  /**
   * Create Paytime account (conta digital)
   * POST /establishment/paytime/create
   */
  async createPaytimeAccount(establishmentId: string): Promise<PaytimeInfo> {
    return apiClient.post<PaytimeInfo>(ROUTES.ESTABLISHMENT.PAYTIME.CREATE, { establishment_id: establishmentId }, { skipToast: true });
  }

  async activatePaytimeSub(establishmentId: string): Promise<PaytimeInfo> {
    return apiClient.post<PaytimeInfo>(ROUTES.ESTABLISHMENT.PAYTIME.SUB_ACTIVATE, { establishment_id: establishmentId }, { skipToast: true });
  }

  // ==================== TAX INTEGRATION ====================

  /**
   * Get tax integration status
   * GET /establishment/tax/integration/status
   */
  async getTaxIntegrationStatus(establishmentId: string): Promise<TaxIntegrationStatus> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', establishmentId);

    return apiClient.get<TaxIntegrationStatus>(`${ROUTES.ESTABLISHMENT.TAX.INTEGRATION_STATUS}?${urlParams.toString()}`);
  }

  /**
   * Get tax configuration
   * GET /establishment/tax/config
   */
  async getTaxConfig(establishmentId: string): Promise<TaxConfigResponse> {
    const urlParams = new URLSearchParams();
    urlParams.append('establishment_id', establishmentId);

    return apiClient.get<TaxConfigResponse>(`${ROUTES.ESTABLISHMENT.TAX.CONFIG}?${urlParams.toString()}`);
  }

  /**
   * Get CST list
   * GET /establishment/tax/cst/list
   */
  async getTaxCstList(): Promise<TaxCstListResponse> {
    return apiClient.get<TaxCstListResponse>(ROUTES.ESTABLISHMENT.TAX.CST_LIST);
  }

  /**
   * Get PIS/COFINS list
   * GET /establishment/tax/pis-cofins/list
   */
  async getPisCofinsList(): Promise<TaxOption[]> {
    return apiClient.get<TaxOption[]>(ROUTES.ESTABLISHMENT.TAX.PIS_COFINS_LIST);
  }

  /**
   * Enable tax integration (NFe)
   * POST /establishment/nfe/integrate
   */
  async enableTaxIntegration(data: EnableTaxIntegrationRequest): Promise<void> {
    return apiClient.post<void>(ROUTES.ESTABLISHMENT.TAX.NFE_INTEGRATE, data, { skipToast: true });
  }

  /**
   * Configure tax integration (NFe)
   * POST /establishment/nfe/activate
   */
  async configureTaxIntegration(data: ConfigureTaxIntegrationRequest): Promise<void> {
    return apiClient.post<void>(ROUTES.ESTABLISHMENT.TAX.NFE_ACTIVATE, data, { skipToast: true });
  }

  /**
   * Delete tax integration (NFe)
   * POST /establishment/nfe/delete
   */
  async deleteTaxIntegration(establishmentId: string): Promise<void> {
    return apiClient.delete<void>(`${ROUTES.ESTABLISHMENT.TAX.NFE_DELETE}?establishment_id=${establishmentId}`, { skipToast: true });
  }

  /**
   * Delete tax certificate
   * POST /establishment/nfe/certificates/delete
   */
  async deleteTaxCertificate(data: DeleteTaxCertificateRequest): Promise<void> {
    return apiClient.post<void>(ROUTES.ESTABLISHMENT.TAX.NFE_CERTIFICATE_DELETE, data, { skipToast: true });
  }

  /**
   * Upload tax certificate
   * POST /establishment/nfe/certificates
   */
  async uploadTaxCertificate(data: UploadTaxCertificateRequest): Promise<void> {
    const formData = new FormData();
    formData.append('establishment_id', data.establishment_id);
    formData.append('nfe_empresa_id', data.nfe_empresa_id);
    formData.append('password', data.password);
    formData.append('file', data.file);

    return apiClient.post<void>(ROUTES.ESTABLISHMENT.TAX.NFE_CERTIFICATE_UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      skipToast: true,
    });
  }
}

// Export singleton instance
export const establishmentService = new EstablishmentService();

// Export class for testing
export { EstablishmentService };
export default establishmentService;
